import * as vars from '@fullstory/proto/fs/api/vars';
import { int64, timestamp } from '@fullstory/proto/google/protobuf';
import { assertExhaustive } from '@fullstory/typeutils';
import {
  DocumentSysFieldTypes,
  EvtSysFieldTypes,
  PageSysFieldTypes,
  UserSysFieldTypes,
} from '@fullstory/client-api/goimports/fs_api_vars';

// Parses unscoped flat vars into a vars array.
export function parseUnscopedFlatVars(
  scope: vars.Scope,
  when: timestamp,
  flatVarsJson: string,
): vars.Var[] {
  if (!flatVarsJson) {
    return [];
  }
  const sysFieldTypes = getSysFieldTypes(scope);
  const asObj = JSON.parse(flatVarsJson);
  const parsed: vars.Var[] = [];
  for (const key in asObj) {
    const { name, type, isSystem } = parseUnscopedVar(key, sysFieldTypes);
    const parsedVar = { scope, name, when, isSystem };
    if (!setValue(parsedVar, asObj[key], type)) {
      console.error(`invalid var ${key}, ignoring...`);
      continue; // ignore for now
    }
    parsed.push(parsedVar);
  }
  return parsed;
}

function getSysFieldTypes(scope: vars.Scope): Map<string, vars.Type> {
  switch (scope) {
    case vars.Scope.INVALID_SCOPE:
      return new Map<string, vars.Type>();
    case vars.Scope.user:
      return UserSysFieldTypes;
    case vars.Scope.evt:
      return EvtSysFieldTypes;
    case vars.Scope.page:
      return PageSysFieldTypes;
    case vars.Scope.document:
      return DocumentSysFieldTypes;
    case vars.Scope.elem:
      return new Map<string, vars.Type>();
    case vars.Scope.fsevt:
      return new Map<string, vars.Type>();
    default:
      assertExhaustive(scope);
  }
}

function parseUnscopedVar(
  rawVar: string,
  sysFieldTypes: Map<string, vars.Type>,
): { name: string; type: vars.Type; isSystem: boolean } {
  let name = rawVar;
  let type = sysFieldTypes.get(name) ?? vars.Type.INVALID_TYPE;
  const isSystem = type != vars.Type.INVALID_TYPE;
  if (!isSystem) {
    // need to parse out the type instead
    const idx = rawVar.lastIndexOf('_');
    if (idx >= 0) {
      const rawType = rawVar.substr(idx + 1);
      name = rawVar.substr(0, idx);
      type = vars.Type[rawType as keyof typeof vars.Type] ?? vars.Type.INVALID_TYPE;
    }
  }
  return { name, type, isSystem };
}

function setValue(parsedVar: vars.Var, objVal: any, type: vars.Type): boolean {
  switch (type) {
    case vars.Type.INVALID_TYPE:
      return false;
    case vars.Type.bool:
      parsedVar.bool = objVal as boolean;
      return true;
    case vars.Type.bools:
      parsedVar.bools = { elements: objVal as boolean[] };
      return true;
    case vars.Type.date:
      parsedVar.date = objVal as timestamp;
      return true;
    case vars.Type.dates:
      parsedVar.dates = { elements: objVal as timestamp[] };
      return true;
    case vars.Type.int:
      parsedVar.int = objVal as int64;
      return true;
    case vars.Type.ints:
      parsedVar.ints = { elements: objVal as int64[] };
      return true;
    case vars.Type.real:
      parsedVar.real = objVal as number;
      return true;
    case vars.Type.reals:
      parsedVar.reals = { elements: objVal as number[] };
      return true;
    case vars.Type.str:
      parsedVar.str = objVal as string;
      return true;
    case vars.Type.strs:
      parsedVar.strs = { elements: objVal as string[] };
      return true;
    default:
      assertExhaustive(type);
  }
}

export function unscopedVarName(name: string, type: vars.Type): string {
  return `${name}_${vars.Type[type]}`;
}

export function multiValueInfo(type: vars.Type): {
  single: vars.Type;
  multi: vars.Type;
  isMulti: boolean;
} {
  switch (type) {
    case vars.Type.INVALID_TYPE:
      return { single: vars.Type.INVALID_TYPE, multi: vars.Type.INVALID_TYPE, isMulti: false };
    case vars.Type.bool:
      return { single: vars.Type.bool, multi: vars.Type.bools, isMulti: false };
    case vars.Type.date:
      return { single: vars.Type.date, multi: vars.Type.dates, isMulti: false };
    case vars.Type.int:
      return { single: vars.Type.int, multi: vars.Type.ints, isMulti: false };
    case vars.Type.real:
      return { single: vars.Type.real, multi: vars.Type.reals, isMulti: false };
    case vars.Type.str:
      return { single: vars.Type.str, multi: vars.Type.strs, isMulti: false };
    case vars.Type.bools:
      return { single: vars.Type.bool, multi: vars.Type.bools, isMulti: true };
    case vars.Type.dates:
      return { single: vars.Type.date, multi: vars.Type.dates, isMulti: true };
    case vars.Type.ints:
      return { single: vars.Type.int, multi: vars.Type.ints, isMulti: true };
    case vars.Type.reals:
      return { single: vars.Type.real, multi: vars.Type.reals, isMulti: true };
    case vars.Type.strs:
      return { single: vars.Type.str, multi: vars.Type.strs, isMulti: true };
    default:
      assertExhaustive(type);
  }
}
